import service from './service'

const state = {
  scope: null, // {type: 'global'|'contract-manager'|'pica-service', id: number}
  templates: [],
  template: null,
  availableTypes: [], // Array of {value, label, description, settings} from API
  availableParameters: {}, // { [emailType]: { param_name: description } }
  debugLogs: [],
  debugLogsPagination: null, // {count, next, previous}
  debugLogsLoading: false
}

const getters = {
  scope: state => state.scope,
  templates: state => state.templates,
  template: state => state.template,
  availableTypes: state => state.availableTypes,
  availableTypeByValue: state => value => state.availableTypes.find(t => t.value === value),
  availableParameters: state => emailType => state.availableParameters[emailType] || {},
  availableSettings: state => emailType => {
    const params = state.availableParameters[emailType]
    return params && params.settings ? params.settings : []
  },
  debugLogs: state => state.debugLogs,
  debugLogsPagination: state => state.debugLogsPagination,
  debugLogsLoading: state => state.debugLogsLoading
}

const mutations = {
  setScope(state, scope) {
    state.scope = scope
  },
  setTemplates(state, templates) {
    state.templates = templates || []
  },
  setTemplate(state, template) {
    state.template = template
  },
  deleteTemplate(state, emailType) {
    state.templates = state.templates.filter(t => t.email_type !== emailType)
  },
  setAvailableTypes(state, types) {
    state.availableTypes = types || []
  },
  setAvailableParameters(state, { emailType, parameters }) {
    state.availableParameters = {
      ...state.availableParameters,
      [emailType]: parameters
    }
  },
  setDebugLogs(state, logs) {
    state.debugLogs = logs || []
  },
  setDebugLogsPagination(state, pagination) {
    state.debugLogsPagination = pagination
  },
  setDebugLogsLoading(state, loading) {
    state.debugLogsLoading = loading
  },
  updateDebugLog(state, updatedLog) {
    const index = state.debugLogs.findIndex(log => log.id === updatedLog.id)
    if (index !== -1) {
      state.debugLogs.splice(index, 1, updatedLog)
    }
  }
}

const actions = {
  /**
   * Set the current scope and fetch templates
   * @param {Object} context
   * @param {Object} scope - {type: 'global'|'contract-manager'|'pica-service', id: number}
   */
  async setScope({ commit, dispatch }, scope) {
    commit('setScope', scope)
    await Promise.all([
      dispatch('fetchAvailableTypes'),
      dispatch('fetchEmailTemplates')
    ])
  },

  /**
   * Fetch all email templates for current scope
   * @param {Object} context
   */
  async fetchEmailTemplates({ commit, state }) {
    if (!state.scope) {
      throw new Error('Scope not set')
    }

    const response = await service.fetchEmailTemplates(state.scope)
    // ApiClient unwraps .data automatically, backend returns {count, results}
    const templates = response.results || []
    commit('setTemplates', templates)
  },

  /**
   * Fetch a single email template
   * @param {Object} context
   * @param {string} emailType
   */
  async fetchEmailTemplate({ commit, state }, emailType) {
    if (!state.scope) {
      throw new Error('Scope not set')
    }

    const data = await service.fetchEmailTemplate(state.scope, emailType)
    commit('setTemplate', data)
  },

  /**
   * Create a new email template
   * @param {Object} context
   * @param {Object} data
   * @returns {Promise<Object>}
   */
  async createEmailTemplate({ commit, state }, data) {
    if (!state.scope) {
      throw new Error('Scope not set')
    }

    const result = await service.createEmailTemplate(state.scope, data)
    commit('setTemplate', result)
    return result
  },

  /**
   * Update an existing email template
   * @param {Object} context
   * @param {Object} payload - {emailType: string, data: Object}
   * @returns {Promise<Object>}
   */
  async updateEmailTemplate({ commit, state }, { emailType, data }) {
    if (!state.scope) {
      throw new Error('Scope not set')
    }

    const result = await service.updateEmailTemplate(state.scope, emailType, data)
    commit('setTemplate', result)
    return result
  },

  /**
   * Delete an email template
   * @param {Object} context
   * @param {string} emailType
   */
  async deleteEmailTemplate({ commit, state }, emailType) {
    if (!state.scope) {
      throw new Error('Scope not set')
    }

    await service.deleteEmailTemplate(state.scope, emailType)
    commit('deleteTemplate', emailType)
  },

  /**
   * Fetch available email types
   * @param {Object} context
   * @returns {Promise<Array>}
   */
  async fetchAvailableTypes({ commit, state }) {
    if (!state.scope) {
      throw new Error('Scope not set')
    }

    const types = await service.fetchAvailableTypes(state.scope)
    // Backend returns array of email type configurations
    commit('setAvailableTypes', types)
    return types
  },

  /**
   * Fetch available parameters for an email type
   * @param {Object} context
   * @param {string} emailType
   * @returns {Promise<Object>}
   */
  async fetchAvailableParameters({ commit, state }, emailType) {
    if (!state.scope) {
      throw new Error('Scope not set')
    }

    const parameters = await service.fetchAvailableParameters(state.scope, emailType)
    commit('setAvailableParameters', { emailType, parameters })
    return parameters
  },

  /**
   * Fetch debug logs for a given email template
   * @param {Object} context
   * @param {Object} payload - {emailType: string, page: number, page_size: number, search: string, sent_status: string}
   */
  async fetchDebugLogs({ commit, state }, { emailType, page = 1, page_size = 20, search = '', sent_status = '' }) {
    if (!state.scope) {
      throw new Error('Scope not set')
    }

    commit('setDebugLogsLoading', true)
    try {
      const params = { page, page_size }
      if (search) params.search = search
      if (sent_status) params.sent_status = sent_status

      const response = await service.fetchDebugLogs(state.scope, emailType, params)
      commit('setDebugLogs', response.results || [])
      commit('setDebugLogsPagination', {
        count: response.count,
        next: response.next,
        previous: response.previous
      })
    } finally {
      commit('setDebugLogsLoading', false)
    }
  },

  /**
   * Export debug logs to Excel
   * @param {Object} context
   * @param {string} emailType
   * @returns {Promise<Blob>}
   */
  async exportDebugLogs({ state }, emailType) {
    if (!state.scope) {
      throw new Error('Scope not set')
    }

    return await service.exportDebugLogs(state.scope, emailType)
  },

  /**
   * Send emails to specific email addresses (creates debug logs)
   * @param {Object} context
   * @param {Object} payload - {emailType: string, emailAddresses: Array<string>, sendOnlyPending: boolean}
   * @returns {Promise<Object>} - {success_count, error_count, errors, total_processed}
   */
  async sendToEmails({ state }, { emailType, emailAddresses, sendOnlyPending = true }) {
    if (!state.scope) {
      throw new Error('Scope not set')
    }

    return await service.sendToEmails(state.scope, emailType, emailAddresses, sendOnlyPending)
  },

  /**
   * Replay selected debug logs
   * @param {Object} context
   * @param {Object} payload - {emailType: string, logIds: Array<number>, recipientAddress?: string}
   * @returns {Promise<Object>} - {success_count, error_count, errors, total_processed}
   */
  async replaySelectedDebugLogs({ state }, { emailType, logIds, recipientAddress = null }) {
    if (!state.scope) {
      throw new Error('Scope not set')
    }

    return await service.replaySelectedDebugLogs(state.scope, emailType, logIds, recipientAddress)
  },

  /**
   * Delete selected debug logs
   * @param {Object} context
   * @param {Object} payload - {emailType: string, logIds: Array<number>}
   * @returns {Promise<Object>} - {deleted_count, message}
   */
  async deleteSelectedDebugLogs({ state }, { emailType, logIds }) {
    if (!state.scope) {
      throw new Error('Scope not set')
    }

    return await service.deleteSelectedDebugLogs(state.scope, emailType, logIds)
  },

  /**
   * Mark debug logs as sent without actually sending them
   * @param {Object} context
   * @param {Object} payload - {emailType: string, logIds: Array<number>|null, all: boolean}
   * @returns {Promise<Object>} - {updated_count, message}
   */
  async markDebugLogsAsSent({ state }, { emailType, logIds = null, all = false }) {
    if (!state.scope) {
      throw new Error('Scope not set')
    }

    return await service.markDebugLogsAsSent(state.scope, emailType, logIds, all)
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
